<template>
	<div class="results-toolbar-error-logs">
		<Search
			placeholder="Search error log details"
			v-model="filters.search"
			:timeout="300"
		/>
		<div class="results-toolbar-error-logs__desktop">
			<Button
				className="btn--link"
				labelText="Reset Filters"
				:btnClick="resetFilters"
			/>
			<DatePicker v-model="filters.dateRange" />
			<div class="results-toolbar-error-logs__desktop-users">
				<DropdownV2
					v-model="filters.user"
					:options="users"
					defaultValue="all"
				/>
			</div>
			<DropdownV2
				v-model="filters.errorCode"
				:options="errorCodes"
				defaultValue="all"
			/>
			<DropdownV2
				v-model="filters.errorStatus"
				:options="errorStatus"
				defaultValue="all"
			/>
		</div>
		<div class="results-toolbar-error-logs__bulk">
			<MenuButton
				@change="bulkAction"
				:value="{
					text: 'Bulk Action',
					value: 'Bulk Action'
				}"
				:items="bulkActions"
				ref="tooltipFeedbackRef"
			/>
			<Tooltip
				:text="'Success! 4 errors resolved.'"
				:variation="'success'"
				:trackedRefName="'tooltipFeedbackRef'"
				:display="true"
				:offset="[-2, 8]"
				position="right-start"
			>
				<template #action
					><Button className="btn--inline-link" labelText="Undo"
				/></template>
			</Tooltip>
		</div>
		<div class="results-toolbar-error-logs__mobile">
			<Button
				className="btn--primary"
				iconClassName="icon-filter"
				:iconBeforeLabel="true"
				:btnClick="() => (displayFiltersModal = true)"
			/>
		</div>
		<div class="results-toolbar-error-logs__counter">
			<ResultCounter :counter="counter" />
		</div>
		<Modal
			v-if="displayFiltersModal"
			:display="true"
			title="Filters"
			@show="displayFiltersModal = false"
		>
			<template #modal-content>
				<div class="results-toolbar-error-logs__modal">
					<DatePicker
						v-model="filtersModal.dateRange"
						labelText="Date Range"
					/>
					<DropdownV2
						v-model="filtersModal.user"
						label="Users"
						:options="users"
						defaultValue="all"
					/>
					<DropdownV2
						v-model="filtersModal.errorCode"
						label="Error Code"
						:options="errorCodes"
						defaultValue="all"
					/>
					<DropdownV2
						v-model="filtersModal.errorStatus"
						label="Status"
						:options="errorStatus"
						defaultValue="all"
					/>
				</div>
			</template>
			<template #modal-footer>
				<ButtonGroup>
					<template #container>
						<Button
							labelText="Apply Filters"
							className="btn--primary"
							:btnClick="applyFilterUsingModal"
							:disabled="!filtersModalHaveChanges"
						/>
						<Button
							labelText="Cancel"
							className="btn--link"
							:btnClick="resetModalFilters"
						/>
					</template>
				</ButtonGroup>
			</template>
		</Modal>
	</div>
</template>

<script>
import Search from '@/components/forms/search/search';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import MenuButton from '@/components/forms/button/menu-button';
import DatePicker from '@/components/forms/date-picker/date-picker';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Modal from '@/components/shells/modal/modals';
import Tooltip from '@/components/shells/tooltip/tooltip';

// libraries
import { deepEqualNoFn } from '@/utils/object';
import _ from 'lodash';

export default {
	name: 'ErrorLogsFilters',
	components: {
		Search,
		ButtonGroup,
		Button,
		MenuButton,
		DatePicker,
		DropdownV2,
		ResultCounter,
		Modal,
		Tooltip
	},
	props: {
		value: {
			type: Object,
			default: () => {}
		},
		defaultFilters: Object,
		counter: Number
	},
	data() {
		return {
			displayFiltersModal: false,
			filters: {},
			filtersModal: {},
			users: [
				{ text: 'All users', value: 'all' },
				{ text: 'User 1', value: '1' },
				{ text: 'User 2', value: '2' },
				{ text: 'User 3', value: '3' },
				{ text: 'User 4', value: '4' }
			],
			errorCodes: [
				{ text: 'All error codes', value: 'all' },
				{ text: 'Error 1', value: '1' },
				{ text: 'Error 2', value: '2' },
				{ text: 'Error 3', value: '3' },
				{ text: 'Error 4', value: '4' }
			],
			errorStatus: [
				{ text: 'All statuses', value: 'all' },
				{ text: 'Unresolved', value: '1' },
				{ text: 'Resolved', value: '2' }
			],
			bulkActions: [
				{ text: 'Mark as resolved', value: 'Mark as resolved' },
				{ text: 'Mark as unresolved', value: 'Mark as unresolved' }
			]
		};
	},
	created() {
		this.filters = _.cloneDeep(this.value);
		this.filtersModal = _.cloneDeep(this.value);
	},
	computed: {
		filtersModalHaveChanges() {
			return !deepEqualNoFn(this.filters, this.filtersModal, true);
		}
	},
	methods: {
		applyFilterUsingModal() {
			this.$emit('input', this.filtersModal);
			this.filters = _.cloneDeep(this.filtersModal);
			this.displayFiltersModal = false;
		},
		resetFilters() {
			this.filters = _.cloneDeep(this.defaultFilters);
		},
		resetModalFilters() {
			this.filtersModal = _.cloneDeep(this.defaultFilters);
			this.displayFiltersModal = false;
		},
		bulkAction(value) {
			console.log(value);
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(newValue) {
				this.filtersModal = _.cloneDeep(newValue);
				this.$emit('input', this.filters);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Error Logs - Filters
 **/

.results-toolbar-error-logs {
	display: grid;
	grid-template-columns: auto auto 1fr;
	column-gap: $gutter-md;
	row-gap: $gutter-lg;
	margin-bottom: $gutter-lg;

	@include mq($from: lg) {
		grid-template-columns: auto 1fr;
	}

	&__mobile,
	&__counter {
		display: flex;
		justify-content: flex-end;
	}

	&__mobile {
		@include mq($from: lg) {
			display: none;
		}
	}

	&__counter {
		grid-column: 1 / 4;
		@include mq($from: lg) {
			grid-column: initial;
			align-items: flex-end;
		}
	}

	&__desktop {
		display: none;
		z-index: 100;

		@include mq($from: lg) {
			display: flex;
			flex-direction: row;
			justify-content: flex-end;
			gap: $gutter-lg;

			.dropdown {
				width: 140px;
			}

			.results-toolbar-error-logs__desktop-users .dropdown {
				width: 240px;
			}
		}
	}

	&__modal {
		width: 540px;
		display: grid;
		grid-template-columns: 50fr 50fr;
		column-gap: $gutter-xxl;
		row-gap: $gutter-xl;
		margin-bottom: $gutter-lg;

		::v-deep .dropdown {
			width: 100%;
		}
	}
}
</style>
